import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { UserProfile } from '../types/userList.types';
import { X, Calendar, MapPin, CreditCard, Mail, Phone, Edit2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/utils/cn';

interface UserQuickDetailProps {
 user: UserProfile;
 onClose: () => void;
}

export function UserQuickDetail({ user, onClose }: UserQuickDetailProps) {
 const navigate = useNavigate();
 const tabs = [
 { id: 'general', label: 'Thông tin chung' },
 { id: 'profile', label: 'Hồ sơ' },
 { id: 'history', label: 'Lịch sử công tác' },
 { id: 'contract', label: 'Hợp đồng' },
 ];

 return (
 <div className="w-[400px] bg-white border-l border-slate-100 flex flex-col h-full animate-in slide-in-from-right duration-300">
 <div className="p-3 border-b border-slate-50 flex items-center justify-between">
 <h3 className="sm:text-sm text-xs text-slate-900 tracking-widest">Thông tin nhân sự</h3>
 <Button 
 variant="ghost" 
 size="icon" 
 onClick={onClose} 
 className="h-8 w-8 text-slate-400 hover:text-slate-600"
 >
 <X className="w-4 h-4" />
 </Button>
 </div>

 <div className="flex-1 overflow-y-auto p-4 scrollbar-hide">
 {/* Profile Header */}
 <div className="flex flex-col items-center text-center mb-6">
 <div className="w-24 h-24 rounded-md bg-slate-100 border-4 border-white mb-4 overflow-hidden">
 {user.avatar_path ? (
 <img src={user.avatar_path} alt="" className="w-full h-full object-cover" />
 ) : (
 <div className="w-full h-full flex items-center justify-center sm:text-lg text-sm text-slate-400 bg-slate-50">
 {user.full_name[0]}
 </div>
 )}
 </div>
 <div className="flex items-center gap-2 mb-1">
 <h2 className="sm:text-base text-sm text-slate-900">{user.full_name}</h2>
 <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-600 sm:text-sm text-xs tracking-wider border border-emerald-100">
 Quản trị viên
 </span>
 </div>
 <p className="sm:text-sm text-xs text-slate-400 tracking-widest mb-3">
 {user.user_code} • {user.gender === 'male' ? 'Nam' : 'Nữ'} • 32 tuổi
 </p>
 <div className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 sm:text-sm text-xs tracking-widest border border-emerald-100">
 Đang làm việc
 </div>
 </div>

 {/* Tabs */}
 <div className="flex gap-4 border-b border-slate-100 mb-6 overflow-x-auto scrollbar-hide">
 {tabs.map(tab => (
 <Button 
 key={tab.id}
 variant="ghost"
 className={cn("pb-3 h-auto px-0 rounded-none sm:text-sm text-xs tracking-widest whitespace-nowrap transition-all border-b-2",
 tab.id === 'general' ?"text-emerald-600 border-emerald-600" :"text-slate-400 border-transparent hover:text-slate-600"
 )}
 >
 {tab.label}
 </Button>
 ))}
 </div>

 {/* Details List */}
 <div className="space-y-4">
 <DetailItem icon={Edit2} label="Họ và tên" value={user.full_name} />
 <DetailItem icon={Calendar} label="Ngày sinh" value={user.birthday || '15/08/1992'} />
 <DetailItem icon={Edit2} label="Giới tính" value={user.gender === 'male' ? 'Nam' : 'Nữ'} />
 <DetailItem icon={Phone} label="Số điện thoại" value={user.phone} />
 <DetailItem icon={Mail} label="Email" value={user.email} />
 <DetailItem icon={CreditCard} label="CCCD/CMND" value={user.id_number || '001092012345'} />
 <DetailItem icon={MapPin} label="Địa chỉ" value={user.address || '12 Nguyễn Trãi, Thanh Xuân, Hà Nội'} />
 <div className="pt-4 border-t border-slate-50 space-y-4">
 <DetailItem icon={Edit2} label="Phòng ban" value={user.department_name || 'Ban quản lý dự án'} />
 <DetailItem icon={Edit2} label="Chức vụ" value={user.position_name || 'Quản lý dự án'} />
 <DetailItem icon={Calendar} label="Ngày vào làm" value={user.join_date || '01/01/2022'} />
 <DetailItem icon={Edit2} label="Hình thức" value="Chính thức" />
 </div>
 </div>
 </div>

 <div className="p-4 border-t border-slate-100 bg-slate-50/30 grid grid-cols-2 gap-3">
 <Button 
 variant="outline" 
 className="h-10 rounded-md gap-2 sm:text-sm text-xs border-slate-200"
 onClick={() => navigate(`/users/${user.id}/edit`)}
 >
 <Edit2 className="w-3.5 h-3.5" />
 <span>Chỉnh sửa</span>
 </Button>
 <Button 
 variant="primary" 
 className="h-10 rounded-md gap-2 sm:text-sm text-xs -500/20"
 onClick={() => navigate(`/users/${user.id}`)}
 >
 <ExternalLink className="w-3.5 h-3.5" />
 <span>Xem chi tiết</span>
 </Button>
 </div>
 </div>
 );
}

function DetailItem({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
 return (
 <div className="flex items-start gap-4 group">
 <div className="p-2 rounded-md bg-slate-50 text-slate-400 group-hover:bg-emerald-50 group-hover:text-emerald-500 transition-all">
 <Icon className="w-3.5 h-3.5" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="sm:text-sm text-xs text-slate-400 tracking-widest mb-0.5">{label}</p>
 <p className="sm:text-sm text-xs text-slate-700 truncate">{value}</p>
 </div>
 </div>
 );
}
